import React, { FC, useCallback, useEffect, useMemo } from "react";
import SidebarMenu from "../../components/UserDashboard/SidebarMenu/SidebarMenu";
import "./UserDashboardPage.scss";
import { useAnchorWallet } from "@solana/wallet-adapter-react";

import {
  getAllStakingAccountsForDashboardClubs,
  getMasterClubsForUser,
  getRealmsForUser,
  getTreasuryDataForClubs,
  getUsersApplications,
  getWithdrawalsData,
} from "../../solana/common/helpers/user.dashboard.helpers";
import DashboardOverview from "../../components/UserDashboard/DashboardOverview/DashboardOverview";
import ClubsTable from "../../components/UserDashboard/ClubsTable/ClubsTable";
import WithdrawalsTable from "../../components/UserDashboard/WithdrawalsTable/WithdrawalsTable";
import TreasuryTable from "../../components/UserDashboard/TreasuryTable/TreasuryTable";
import InvitationsTable from "../../components/UserDashboard/InvitationsTable/InvitationsTable";
import { InvitationStatus } from "../../common/enums/club.enum";
import StakingTable from "../../components/UserDashboard/StakingTable/StakingTable";

import ProposalsDashboard from "../../components/UserDashboard/ProposalsDashboard/ProposalsDashboard";
import { DashboardActiveStep } from "../../common/enums/dashboard.enums";

import { USER_ACCOUNT_SETTINGS } from "../../common/constants/routes.constants";

import UserAccountSettings from "../UserAccountSettings/UserAccountSettings";
import { dashboardStore } from "../../contexts/dashboardStore";
import { IDashboardTokenAccountInfo } from "../../common/interfaces/user-dashboard.interface";
import { getTreasuyTokenAccountsInfo } from "../../solana/common/helpers/common.helpers";
import { Helmet } from "react-helmet-async";

const UserDashboardPage: FC = () => {
  const wallet = useAnchorWallet();
  const {
    activeStep,
    setInitialLoading,
    setWithdrawals,
    setDeposits,
    setApplications,
    setStakings,
    setInvitations,
    setActiveStep,
    setClubs,
    setTreasuryData,
    resetState,
    setTreasuryTokensLoading,
    setTreasuryTokens,
    setMasterClubs,
  } = dashboardStore();

  useEffect(() => {
    void setDashboardData();

    return () => {
      resetState();
    };
  }, [wallet?.publicKey]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    changeActiveOption();
  }, [window.location.href]);

  const changeActiveOption = () => {
    if (window.location.href.includes(USER_ACCOUNT_SETTINGS)) {
      setActiveStep(DashboardActiveStep.Account);
    } else {
      setActiveStep(DashboardActiveStep.Dashboard);
    }
  };

  const setDashboardData = useCallback(async () => {
    if (wallet) {
      try {
        setInitialLoading(true);
        const userRealms = await getRealmsForUser(wallet!);
        const myClubs = userRealms.filter(
          (realm) => realm.invitationStatus === InvitationStatus.Accepted
        );
        const invitedClubs = userRealms.filter(
          (realm) => realm.invitationStatus === InvitationStatus.Invited
        );
        setClubs(myClubs);
        const masterClubs = await getMasterClubsForUser(wallet!);
        setMasterClubs(masterClubs);
        setInvitations(invitedClubs);
        setApplications(await getUsersApplications(userRealms!, wallet));
        setStakings(await getAllStakingAccountsForDashboardClubs(wallet));
        const { deposits, treasuryInfo } = await getTreasuryDataForClubs(
          myClubs,
          wallet!.publicKey
        );
        setTreasuryData(treasuryInfo);
        setDeposits(deposits);
        const allWithdrawals = await getWithdrawalsData(
          myClubs,
          treasuryInfo,
          wallet,
          deposits
        );
        setWithdrawals(allWithdrawals);
        setInitialLoading(false);
        setTreasuryTokensLoading(true);
        const tokens: IDashboardTokenAccountInfo[] = [];
        for (const treasury of treasuryInfo) {
          if (treasury) {
            const treasuryTokenAccounts = await getTreasuyTokenAccountsInfo(
              treasury.treasuryAddress,
              treasury.treasuryLamports
            );
            tokens.push({
              tokenAccounts: treasuryTokenAccounts,
              treasuryAddress: treasury.treasuryAddress,
            });
          }
        }
        setTreasuryTokens(tokens);
        setTreasuryTokensLoading(false);
      } catch (error) {
        resetState();
        setInitialLoading(false);
      }
    }
  }, [wallet?.publicKey]);

  const renderActiveStep = useMemo(() => {
    switch (activeStep) {
      case DashboardActiveStep.Dashboard:
        return <DashboardOverview />;
      case DashboardActiveStep.Clubs:
        return <ClubsTable />;
      case DashboardActiveStep.Withdrawals:
        return <WithdrawalsTable />;
      case DashboardActiveStep.Treasuries:
        return <TreasuryTable />;
      case DashboardActiveStep.Invitations:
        return <InvitationsTable />;
      case DashboardActiveStep.Staking:
        return <StakingTable />;
      case DashboardActiveStep.Proposals:
        return <ProposalsDashboard />;
      case DashboardActiveStep.Account:
        return <UserAccountSettings />;
      default:
        return <h1>Not found</h1>;
    }
  }, [activeStep]);

  return (
    <div className="user-dashboard-page">
      <Helmet>
        <title>Monke Ventures</title>
      </Helmet>
      <SidebarMenu />
      <div className="user-dashboard-page__content">
        <p className="user-dashboard-page__title">{activeStep}</p>
        {wallet?.publicKey ? (
          renderActiveStep
        ) : (
          <div className="user-dashboard-page__wallet-disconnected">
            <h3 className="create-club__label">Please connect your wallet</h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboardPage;
